const isEmpty = (value) =>
  value === null || value === undefined || value === "";

// "00:00:02" (hh:mm:ss), "05:30" (mm:ss) or a plain number of seconds.
const durationToSeconds = (duration) => {
  if (typeof duration === "number") return duration;
  if (!duration) return 0;

  const parts = String(duration).split(":").map(Number);
  if (parts.some((part) => !Number.isFinite(part))) return 0;
  return parts.reduce((total, part) => total * 60 + part, 0);
};

/*
 * Only the `duration` column is used. Records that never had a `duration`
 * (older attempts) have no reliable time, so nothing is shown for them.
 */
export const hasRecordedDuration = (question) => !isEmpty(question?.duration);

const unit = (value, name) => `${value} ${name}${value === 1 ? "" : "s"}`;

const formatSeconds = (totalSeconds, alwaysShowMinutes) => {
  const seconds = Math.max(0, Math.round(totalSeconds || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = seconds % 60;

  if (hours > 0) {
    return `${unit(hours, "hour")} ${unit(minutes, "minute")} ${unit(rest, "second")}`;
  }
  if (minutes > 0 || alwaysShowMinutes) {
    return `${unit(minutes, "minute")} ${unit(rest, "second")}`;
  }
  return unit(rest, "second");
};

// "2 seconds", "1 minute 5 seconds"; null when no duration was recorded.
export const formatQuestionDuration = (question) =>
  hasRecordedDuration(question)
    ? formatSeconds(durationToSeconds(question.duration), false)
    : null;

const DAY_SECONDS = 24 * 60 * 60;

// "13:44:49" (time of day) or a full date/time, as seconds.
const toSeconds = (value) => {
  if (isEmpty(value)) return null;

  const text = String(value);
  const time = text.match(/^(\d{1,3}):(\d{2}):(\d{2})/);

  if (time && !text.includes("-")) {
    return Number(time[1]) * 3600 + Number(time[2]) * 60 + Number(time[3]);
  }

  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms / 1000;
};

// Earliest to latest answer time (last_time), handling a run past midnight.
const getAnswerTimeSpan = (questions) => {
  const times = questions
    .map((question) => toSeconds(question.last_time))
    .filter((value) => value !== null);

  if (times.length < 2) return 0;

  let earliest = Math.min(...times);
  let latest = Math.max(...times);

  if (latest - earliest > DAY_SECONDS / 2 && latest < DAY_SECONDS) {
    const shifted = times.map((value) =>
      value < DAY_SECONDS / 2 ? value + DAY_SECONDS : value
    );
    earliest = Math.min(...shifted);
    latest = Math.max(...shifted);
  }

  return Math.max(0, Math.round(latest - earliest));
};

/*
 * Total time spent on an attempt:
 * 1. the sum of each question's `duration`;
 * 2. if there are no durations (older attempts), the gap between the earliest
 *    and latest answer time (last_time).
 * Returns null when neither can be worked out. `duration_old` is not used.
 */
export const getTimeSpentSeconds = (questions = []) => {
  const recorded = questions.filter(hasRecordedDuration);
  const durationTotal = Math.round(
    recorded.reduce(
      (total, question) => total + durationToSeconds(question.duration),
      0
    )
  );
  if (durationTotal > 0) return durationTotal;

  const span = getAnswerTimeSpan(questions);
  if (span > 0) return span;

  return recorded.length > 0 ? 0 : null;
};

export const formatTimeSpent = (totalSeconds) =>
  totalSeconds === null || totalSeconds === undefined
    ? null
    : formatSeconds(totalSeconds, true);
